import { 
    Outlet, 
    Navigate, 
    useLocation 
} from "react-router-dom"
import "./Main.css"

// import Sidebar from "../../components/common/Sidebar/Sidebar"
import Navbar from "@/components/common/Navbar/Navbar"
import Footer from "../../components/common/Footer/Footer"
import BottomTabs from "@/components/common/BottomTabs/BottomTabs"
import Spinner from "@/components/common/Spinner/Spinner"

import routes from '@/routes/routes'
import useAuthStore from "@/stores/AuthStore"
import { useProfileStatus } from "@/hooks/useProfile"
import { useEffect, useMemo } from 'react'
import { useServiceRequests } from '@/hooks/useRequests'
import useModalStore from '@/stores/ModalStore'
import ServiceConfirmation from '@/modals/ServiceConfirmation/ServiceConfirmation'
import ReviewModal from '@/modals/ReviewModal/ReviewModal'
import ProviderOnboarding from '@/components/onboarding/ProviderOnboarding'

const Main = () => {

    const location = useLocation()
    const { isLogged } = useAuthStore()
    const { data: profileStatus, isLoading: statusLoading, refetch: refetchProfileStatus } = useProfileStatus()
    const { data: requests } = useServiceRequests()
    const setModalContent = useModalStore((s) => s.setModalContent)
    const isModalOpen = useModalStore((s) => s.isModalOpen)

    // Refetch profile status when component mounts to ensure fresh data
    useEffect(() => {
        if (isLogged) {
            refetchProfileStatus()
        }
    }, [isLogged, refetchProfileStatus])

    const pendingConfirmation = useMemo(() => {
        if (!isLogged || !Array.isArray(requests)) return null
        return requests.find((r: any) => r.status === 'completed' && !r.client_confirmed_satisfaction)
    }, [isLogged, requests])

    const pendingReview = useMemo(() => {
        if (!isLogged || !Array.isArray(requests)) return null
        return requests.find((r: any) => r.status === 'completed' && r.review_prompted_at && !r.reviews)
    }, [isLogged, requests])

    useEffect(() => {
        if (pendingConfirmation && !isModalOpen) {
            setModalContent(
                <ServiceConfirmation
                    serviceRequestId={pendingConfirmation.id}
                    serviceName={pendingConfirmation.service_name || pendingConfirmation.service?.name || 'Servicio'}
                    providerName={pendingConfirmation.provider_email || pendingConfirmation.provider_info?.first_name || 'Proveedor'}
                />
            )
        } else if (pendingReview && !isModalOpen && !pendingConfirmation) {
            setModalContent(
                <ReviewModal
                    serviceRequestId={pendingReview.id}
                    serviceName={pendingReview.service_name || pendingReview.service?.name || 'Servicio'}
                    providerName={pendingReview.provider_email || pendingReview.provider_info?.first_name || 'Proveedor'}
                    handleClose={() => {
                        useModalStore.getState().closeModal()
                    }}
                />
            )
        }
    }, [pendingConfirmation, pendingReview, isModalOpen, setModalContent])

    // NOTE this is for adding conditions like isLogged, isAdmin and Navigate the user to some page

    // Check if provider needs onboarding
    const needsOnboarding = isLogged && profileStatus?.is_provider && (!profileStatus.has_services || !profileStatus.has_districts)

    // Only show the landing page on the exact root URL
    const isLandingPage = location.pathname === "/"
    const isAuthPath = [
        routes.auth,
        routes.verifyEmail,
        routes.forgotPassword,
        routes.changePassword,
    ].includes(location.pathname)

    // If onboarding is needed, show it without Navbar/Footer
    if (needsOnboarding && !isAuthPath && !isLandingPage) {
        return <ProviderOnboarding />
    }

    return (
        <>
            {!isLandingPage ? (
                <div className="main-app-container">
                    {/* <Sidebar /> */}
                    {!isAuthPath && <Navbar/>}
                    <div className="main-content-wrapper">
                        {/* If not logged and hitting a non-auth page, go to auth */}
                        {!isAuthPath && !isLogged ? (
                            <Navigate to={routes.auth} />
                        ) : (
                            <>
                                <Outlet />
                                {!isAuthPath && <Footer />}
                                {!isAuthPath && isLogged && <BottomTabs />}
                            </>
                        )}
                    </div>
                </div>
            ) : (
                // Root navigation logic
                // - If not logged in: go to auth login
                // - If logged in and provider: go to provider requests
                // - If logged in and client: go to service search
                statusLoading ? (
                    <div className="loading-screen">
                        <Spinner variant="primary" size="lg" />
                    </div>
                ) : (
                    !isLogged ? (
                        <Navigate to={routes.auth} />
                    ) : profileStatus?.is_provider ? (
                        // Check if provider needs onboarding
                        (profileStatus && (!profileStatus.has_services || !profileStatus.has_districts)) ? (
                            <ProviderOnboarding />
                        ) : (
                        <Navigate to={routes.providerRequests} />
                        )
                    ) : (
                        <Navigate to={routes.serviceSearch} />
                    )
                )
            )}
        </>
    )
}

export default Main